import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { API_URL, getHeaders, Spinner, Empty, Pagination, PAGE_SIZE, VALUE_BUCKETS, getBucket } from './shared';
import { InlineAccessRequest } from './InlineAccessRequest';
import logger from '../../utils/logger';

export function AnonymizedPortfolios({ adminRole }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [openCountries, setOpenCountries] = useState({});
  const [activeBucket, setActiveBucket] = useState('all');
  const [countryPages, setCountryPages] = useState({});

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/portfolios/anonymized`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch');
      setData(await res.json());
    } catch (err) { logger.error(err); toast.error('Failed to load portfolios'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  const { grouped, bucketCounts, countries } = useMemo(() => {
    if (!data) return { grouped: {}, bucketCounts: {}, countries: [] };
    const bucketCounts = { all: data.portfolios.length };
    VALUE_BUCKETS.forEach(b => { bucketCounts[b.key] = 0; });
    const filtered = data.portfolios.filter(p => {
      const bk = getBucket(p.total_value);
      bucketCounts[bk] = (bucketCounts[bk] || 0) + 1;
      return activeBucket === 'all' || bk === activeBucket;
    });
    const grouped = {};
    filtered.forEach(p => {
      const country = p.country || 'Unspecified';
      if (!grouped[country]) grouped[country] = [];
      grouped[country].push(p);
    });
    const countries = Object.keys(grouped).sort((a, b) => {
      if (a === 'Unspecified') return 1;
      if (b === 'Unspecified') return -1;
      return a.localeCompare(b);
    });
    return { grouped, bucketCounts, countries };
  }, [data, activeBucket]);

  const toggleCountry = (c) => { setOpenCountries(prev => ({ ...prev, [c]: !prev[c] })); setExpanded(null); };
  const getPage = (country) => countryPages[country] || 1;
  const setPage = (country, page) => setCountryPages(prev => ({ ...prev, [country]: page }));

  if (loading) return <Spinner text="Loading anonymized portfolios..." />;
  if (!data) return <Empty text="No portfolio data" />;

  const isOwnerView = data.viewer_role === 'owner' || adminRole === 'owner';

  return (
    <div className="space-y-4">
      {isOwnerView && (
        <div className="bg-amber-900/20 border border-amber-700 rounded-xl p-3 flex items-start gap-2">
          <span className="text-amber-400 text-sm">⚠</span>
          <p className="text-xs text-amber-300">Owner view: real identities are shown. All access is audit-logged.</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Users', val: data.total_users },
          { label: 'Platform Value', val: `$${data.total_platform_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Avg Portfolio', val: `$${data.average_portfolio_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
        ].map(c => (
          <div key={c.label} className="bg-gray-800 border border-gray-700 rounded-xl p-4" data-testid={`summary-${c.label.toLowerCase().replace(/ /g, '-')}`}>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{c.label}</p>
            <p className="text-xl font-mono font-bold text-white">{c.val}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button data-testid="bucket-all" onClick={() => { setActiveBucket('all'); setCountryPages({}); }}
          className={`px-4 py-1.5 rounded-lg text-xs font-medium transition ${activeBucket === 'all' ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
          All ({bucketCounts.all || 0})
        </button>
        {VALUE_BUCKETS.map(b => (
          <button key={b.key} data-testid={`bucket-${b.key}`} onClick={() => { setActiveBucket(b.key); setCountryPages({}); }}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition ${activeBucket === b.key ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
            {b.label} ({bucketCounts[b.key] || 0})
          </button>
        ))}
      </div>

      {countries.length === 0 ? <Empty text="No portfolios in this range" /> : (
        <div className="space-y-2">
          {countries.map(country => {
            const items = grouped[country];
            const isOpen = openCountries[country] !== false;
            const page = getPage(country);
            const totalPages = Math.ceil(items.length / PAGE_SIZE);
            const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
            return (
              <div key={country} className="border border-gray-700 rounded-xl overflow-hidden" data-testid={`country-group-${country}`}>
                <button data-testid={`country-toggle-${country}`} onClick={() => toggleCountry(country)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-750 transition text-left">
                  <div className="flex items-center gap-3">
                    <span className="text-white font-medium text-sm">{country}</span>
                    <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">{items.length} users</span>
                  </div>
                  <span className="text-gray-500 text-xs">{isOpen ? '\u25B2' : '\u25BC'}</span>
                </button>
                {isOpen && (
                  <div className="bg-gray-900/50 p-3 space-y-2">
                    {pageItems.map((p, i) => {
                      const globalKey = `${country}-${page}-${i}`;
                      return (
                        <div key={globalKey} data-testid={`portfolio-card-${globalKey}`} className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                          <button data-testid={`portfolio-expand-${globalKey}`} onClick={() => setExpanded(expanded === globalKey ? null : globalKey)}
                            className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-750 transition">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-mono font-bold">
                                {isOwnerView && p.name
                                  ? p.name.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()
                                  : p.anonymous_id.replace('User #', '').slice(0, 3)}
                              </div>
                              <div>
                                {isOwnerView ? (
                                  <>
                                    <p className="font-medium text-white text-sm">{p.name || 'Unknown'}</p>
                                    <p className="text-xs text-gray-400">{p.email} · {p.investment_count} investments</p>
                                  </>
                                ) : (
                                  <>
                                    <p className="font-medium text-white font-mono text-sm">{p.anonymous_id}</p>
                                    <p className="text-xs text-gray-400">{p.investment_count} investments</p>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-sm font-mono font-medium text-white">${p.total_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                <p className={`text-xs font-mono ${p.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {p.profit_loss >= 0 ? '+' : ''}{p.profit_loss_percentage}%
                                </p>
                              </div>
                              <span className="text-gray-500 text-xs">{expanded === globalKey ? '\u25B2' : '\u25BC'}</span>
                            </div>
                          </button>
                          {expanded === globalKey && (
                            <div className="px-3 pb-3 border-t border-gray-700 pt-3 space-y-3">
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                <div><p className="text-xs text-gray-500">Total Invested</p><p className="text-white font-mono">${p.total_invested?.toLocaleString()}</p></div>
                                <div><p className="text-xs text-gray-500">P/L</p><p className={`font-mono ${p.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>${p.profit_loss?.toLocaleString()}</p></div>
                                <div><p className="text-xs text-gray-500">Account Age</p><p className="text-white font-mono">{p.account_age_days} days</p></div>
                                <div><p className="text-xs text-gray-500">2FA</p><p className="text-white">{p.has_2fa ? 'Enabled' : 'Disabled'}</p></div>
                              </div>
                              {Object.keys(p.asset_breakdown || {}).length > 0 && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Asset Breakdown</p>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(p.asset_breakdown).map(([type, info]) => (
                                      <span key={type} className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded-full font-mono">
                                        {type.replace(/_/g, ' ')}: {info.count} (${info.total_value?.toLocaleString()})
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {adminRole !== 'owner' && <InlineAccessRequest anonymousId={p.anonymous_id} />}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <Pagination page={page} totalPages={totalPages} onPageChange={(p) => setPage(country, p)} label={`${items.length} portfolios in ${country}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
